import { useState, useEffect } from 'react';
import API from '../services/api';
import {
  Save, Bot, Key, Send, Users, CreditCard, Copy, Trash2, RefreshCw,
  CheckCircle, AlertCircle, Eye, EyeOff, Sliders
} from 'lucide-react';

const STATUS_OPTIONS = ['Bestellt', 'Erstattet', 'Eingereicht', 'Privat', 'Storno', 'Reviewed'];
const PLATFORM_OPTIONS = ['WeChat', 'Telegram', 'WhatsApp', 'Facebook', 'Sonstiges'];
const MESSENGER_EMOJIS = { WeChat: '💬', Telegram: '✈️', WhatsApp: '📱', Facebook: '👤', Sonstiges: '💭' };

const AGE_OPTIONS       = ['20er','30er','40er','50+'];
const GENDER_OPTIONS    = ['weiblich','maennlich','neutral'];
const STYLE_OPTIONS     = ['locker','sachlich','humorvoll','skeptisch','detailliert'];
const HOUSEHOLD_OPTIONS = ['single','paerchen','familie','rentner'];
const STYLE_LABELS      = { locker:'Locker', sachlich:'Sachlich', humorvoll:'Humorvoll', skeptisch:'Skeptisch', detailliert:'Detailliert' };
const HOUSEHOLD_LABELS  = { single:'Single', paerchen:'Pärchen', familie:'Familie', rentner:'Rentner/in' };
const GENDER_LABELS     = { weiblich:'Weiblich', maennlich:'Männlich', neutral:'Neutral' };

function Section({ title, icon: Icon, children }) {
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:20, paddingBottom:16, borderBottom:'1px solid var(--border)' }}>
        <div style={{ background:'rgba(124,106,247,0.15)', borderRadius:8, padding:8, display:'flex' }}>
          <Icon size={16} color="var(--accent)" />
        </div>
        <h2 style={{ fontFamily:'Syne', fontSize:16, fontWeight:700 }}>{title}</h2>
      </div>
      {children}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom:16 }}>
      <label style={{ display:'block', marginBottom:6, color:'var(--text2)', fontSize:12, fontFamily:'Syne', textTransform:'uppercase', letterSpacing:'0.06em' }}>
        {label}
      </label>
      {children}
      {hint && <p style={{ color:'var(--text3)', fontSize:11, marginTop:4 }}>{hint}</p>}
    </div>
  );
}

function SecretInput({ value, onChange, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position:'relative' }}>
      <input type={show ? 'text' : 'password'} value={value} onChange={onChange} placeholder={placeholder} style={{ paddingRight:40 }} />
      <button onClick={() => setShow(!show)} style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'var(--text3)', display:'flex', padding:0 }}>
        {show ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
    </div>
  );
}

function PillGroup({ options, value, onChange, renderLabel }) {
  return (
    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
      {options.map(opt => (
        <button key={opt} onClick={() => onChange(opt)} className="btn" style={{ background: value===opt ? 'var(--accent)':'transparent', color: value===opt ? 'white':'var(--text2)', border: value===opt ? 'none':'1px solid var(--border)', padding:'6px 14px', fontSize:12 }}>
          {renderLabel ? renderLabel(opt) : opt}
        </button>
      ))}
    </div>
  );
}

const LENGTH_OPTIONS = ['kurz', 'mittel', 'lang'];
const LENGTH_LABELS = { kurz: 'Kurz (80-120 Wörter)', mittel: 'Mittel (120-170 Wörter)', lang: 'Lang (170-220 Wörter)' };

function PersonaCard({ accountName, persona, onSave, savingAccount }) {
  const empty = { name:'', age_group:'', gender:'', writing_style:'', household:'', auto_review:false, review_length:'mittel', persona_description:'', custom_rules:'' };
  const [p, setP] = useState({ ...empty, ...(persona || {}) });
  const [localSaved, setLocalSaved] = useState(false);

  const isConfigured = !!(persona?.age_group || persona?.gender || persona?.writing_style || persona?.household);
  const [open, setOpen] = useState(isConfigured);

  useEffect(() => {
    const cfg = { ...empty, ...(persona || {}) };
    setP(cfg);
    setOpen(!!(persona?.age_group || persona?.gender || persona?.writing_style || persona?.household));
  }, [persona]);

  const set = (key, val) => {
    setP(prev => {
      const next = { ...prev, [key]: val };
      // Wenn auto_review eingeschaltet wird und vorher aus war → Datum setzen
      if (key === 'auto_review' && val === true && !prev.auto_review) {
        next.auto_review_since = new Date().toISOString().split('T')[0];
      }
      // Wenn auto_review ausgeschaltet wird → Datum löschen
      if (key === 'auto_review' && val === false) {
        next.auto_review_since = null;
      }
      return next;
    });
    setLocalSaved(false);
  };
  const toggle = (key, val) => set(key, p[key] === val ? '' : val);

  // Freitext sanitizen — nur harmlose Zeichen erlaubt
  const sanitize = (text, max) => text.replace(/[<>{}\\]/g, '').slice(0, max);

  const handleSave = async () => {
    await onSave(accountName, p);
    setLocalSaved(true);
    setTimeout(() => setLocalSaved(false), 2000);
  };

  const isSaving = savingAccount === accountName;
  const hasContent = p.age_group || p.gender || p.writing_style || p.household;

  return (
    <div style={{ background:'var(--bg3)', border:`1px solid ${hasContent ? 'rgba(124,106,247,0.3)':'var(--border)'}`, borderRadius:10, marginBottom:8, overflow:'hidden' }}>
      {/* Header — immer sichtbar */}
      <div onClick={() => setOpen(o => !o)} style={{ padding:'10px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ background: hasContent ? 'var(--accent)':'var(--bg2)', color: hasContent ? 'white':'var(--text3)', borderRadius:6, padding:'2px 10px', fontFamily:'Syne', fontWeight:700, fontSize:12, border: hasContent ? 'none':'1px solid var(--border)' }}>
            {accountName}
          </span>
          {p.name && <span style={{ fontSize:12, color:'var(--text2)' }}>{p.name}</span>}
          {hasContent && (
            <span style={{ fontSize:11, color:'var(--text3)' }}>
              {[p.age_group, p.gender && GENDER_LABELS[p.gender], p.writing_style && STYLE_LABELS[p.writing_style]].filter(Boolean).join(' · ')}
            </span>
          )}
          {p.auto_review && <span style={{ fontSize:10, color:'var(--accent2)', fontWeight:600 }}>● Auto</span>}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          {open && (
            <button onClick={e => { e.stopPropagation(); handleSave(); }} disabled={isSaving} className="btn btn-primary" style={{ padding:'3px 12px', fontSize:11 }}>
              {isSaving ? '...' : localSaved ? '✓' : 'Speichern'}
            </button>
          )}
          <span style={{ color:'var(--text3)', fontSize:12 }}>{open ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* Body — collapsible */}
      {open && (
        <div style={{ padding:'0 16px 14px', borderTop:'1px solid var(--border)' }}>
          {/* Name + Auto-Review */}
          <div style={{ display:'flex', alignItems:'center', gap:12, margin:'12px 0 10px' }}>
            <input
              value={p.name || ''}
              onChange={e => set('name', e.target.value)}
              placeholder="Personenname (optional)"
              style={{ background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:6, padding:'5px 10px', color:'var(--text)', fontSize:12, width:180, outline:'none' }}
            />
            <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'var(--text2)', cursor:'pointer' }}>
  <input type="checkbox" checked={!!p.auto_review} onChange={e => set('auto_review', e.target.checked)} style={{ width:'auto', accentColor:'var(--accent)', cursor:'pointer' }} />
  Auto-Review aktiv
</label>
{p.auto_review && (
  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
    <span style={{ fontSize:11, color:'var(--text3)' }}>ab</span>
    <input
      type="date"
      value={p.auto_review_since ? p.auto_review_since.split('T')[0] : new Date().toISOString().split('T')[0]}
      onChange={e => set('auto_review_since', e.target.value)}
      style={{ background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:6, padding:'3px 8px', color:'var(--text)', fontSize:11, outline:'none' }}
    />
  </div>
)}
          </div>

          {/* Bausteine Grid */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:12 }}>
            <div>
              <div style={{ fontSize:10, color:'var(--text3)', textTransform:'uppercase', letterSpacing:'0.4px', marginBottom:5 }}>Alter</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                {AGE_OPTIONS.map(opt => (
                  <button key={opt} onClick={() => toggle('age_group', opt)} className="btn" style={{ padding:'3px 8px', fontSize:11, background: p.age_group===opt ? 'var(--accent)':'var(--bg2)', color: p.age_group===opt ? 'white':'var(--text2)', border: p.age_group===opt ? 'none':'1px solid var(--border)' }}>{opt}</button>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize:10, color:'var(--text3)', textTransform:'uppercase', letterSpacing:'0.4px', marginBottom:5 }}>Geschlecht</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                {GENDER_OPTIONS.map(opt => (
                  <button key={opt} onClick={() => toggle('gender', opt)} className="btn" style={{ padding:'3px 8px', fontSize:11, background: p.gender===opt ? 'var(--accent)':'var(--bg2)', color: p.gender===opt ? 'white':'var(--text2)', border: p.gender===opt ? 'none':'1px solid var(--border)' }}>{GENDER_LABELS[opt]}</button>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize:10, color:'var(--text3)', textTransform:'uppercase', letterSpacing:'0.4px', marginBottom:5 }}>Schreibstil</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                {STYLE_OPTIONS.map(opt => (
                  <button key={opt} onClick={() => toggle('writing_style', opt)} className="btn" style={{ padding:'3px 8px', fontSize:11, background: p.writing_style===opt ? 'var(--accent)':'var(--bg2)', color: p.writing_style===opt ? 'white':'var(--text2)', border: p.writing_style===opt ? 'none':'1px solid var(--border)' }}>{STYLE_LABELS[opt]}</button>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize:10, color:'var(--text3)', textTransform:'uppercase', letterSpacing:'0.4px', marginBottom:5 }}>Haushalt</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                {HOUSEHOLD_OPTIONS.map(opt => (
                  <button key={opt} onClick={() => toggle('household', opt)} className="btn" style={{ padding:'3px 8px', fontSize:11, background: p.household===opt ? 'var(--accent)':'var(--bg2)', color: p.household===opt ? 'white':'var(--text2)', border: p.household===opt ? 'none':'1px solid var(--border)' }}>{HOUSEHOLD_LABELS[opt]}</button>
                ))}
              </div>
            </div>
          </div>

          {/* Reviewlänge */}
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:10, color:'var(--text3)', textTransform:'uppercase', letterSpacing:'0.4px', marginBottom:5 }}>Reviewlänge</div>
            <div style={{ display:'flex', gap:6 }}>
              {LENGTH_OPTIONS.map(opt => (
                <button key={opt} onClick={() => set('review_length', opt)} className="btn" style={{ padding:'4px 12px', fontSize:11, background: (p.review_length||'mittel')===opt ? 'var(--accent)':'var(--bg2)', color: (p.review_length||'mittel')===opt ? 'white':'var(--text2)', border: (p.review_length||'mittel')===opt ? 'none':'1px solid var(--border)' }}>
                  {LENGTH_LABELS[opt]}
                </button>
              ))}
            </div>
          </div>

          {/* Eigene Persona-Beschreibung */}
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:10, color:'var(--text3)', textTransform:'uppercase', letterSpacing:'0.4px', marginBottom:5 }}>
              Eigene Persona-Beschreibung <span style={{ color:'var(--text3)', fontWeight:400, textTransform:'none', fontSize:10 }}>(optional, max. 1500 Zeichen)</span>
            </div>
            <textarea
              value={p.persona_description || ''}
              onChange={e => set('persona_description', sanitize(e.target.value, 1500))}
              placeholder="z.B. 'Nutzt Technik viel im Alltag, schreibt eher direkt und praktisch. Kleine Schwächen werden offen erwähnt. Bei Haushaltsprodukten zählt vor allem, ob sie im täglichen Einsatz unkompliziert funktionieren.'"
              rows={4}
              style={{ width:'100%', background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:8, padding:'8px 12px', color:'var(--text)', fontSize:12, resize:'vertical', outline:'none', boxSizing:'border-box' }}
            />
            <div style={{ fontSize:10, color:'var(--text3)', marginTop:3, textAlign:'right' }}>{(p.persona_description||'').length}/1500</div>
          </div>

          {/* Eigene Zusatzregeln */}
          <div>
            <div style={{ fontSize:10, color:'var(--text3)', textTransform:'uppercase', letterSpacing:'0.4px', marginBottom:5 }}>
              Zusätzliche Schreibregeln <span style={{ color:'var(--text3)', fontWeight:400, textTransform:'none', fontSize:10 }}>(optional, max. 1000 Zeichen)</span>
            </div>
            <textarea
              value={p.custom_rules || ''}
              onChange={e => set('custom_rules', sanitize(e.target.value, 1000))}
              placeholder="z.B. 'Keine übertriebenen Formulierungen. Nicht jedes Review mit Ich habe ... beginnen. Kleine Kritikpunkte ruhig erwähnen.'"
              rows={3}
              style={{ width:'100%', background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:8, padding:'8px 12px', color:'var(--text)', fontSize:12, resize:'vertical', outline:'none', boxSizing:'border-box' }}
            />
            <div style={{ fontSize:10, color:'var(--text3)', marginTop:3, textAlign:'right' }}>{(p.custom_rules||'').length}/1000</div>
          </div>
        </div>
      )}
    </div>
  );
}

function BatchGenerateSection({ showToast }) {
  const [fromDate, setFromDate] = useState(new Date().toISOString().split('T')[0]);
  const [running, setRunning] = useState(false);
  const [runningAuto, setRunningAuto] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setRunning(true);
    setResult(null);
    try {
      const res = await API.post('/reviews/generate-batch', { from_date: fromDate });
      setResult({ ...res.data, type: 'batch' });
    } catch (err) {
      showToast(err.response?.data?.error || 'Fehler beim Generieren.', 'error');
    } finally {
      setRunning(false);
    }
  };

  const runAuto = async () => {
    setRunningAuto(true);
    setResult(null);
    try {
      const res = await API.post('/reviews/run-auto');
      setResult({ ...res.data, type: 'auto' });
    } catch (err) {
      showToast(err.response?.data?.error || 'Fehler.', 'error');
    } finally {
      setRunningAuto(false);
    }
  };

  return (
    <>
      {/* Einmalig Batch */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'Syne', textTransform: 'uppercase', marginBottom: 8 }}>
          Einmalig — alle Bestellungen ab Datum
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <div>
            <label style={{ display: 'block', marginBottom: 6, color: 'var(--text2)', fontSize: 11, fontFamily: 'Syne', textTransform: 'uppercase' }}>
              Ab Datum
            </label>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ maxWidth: 180 }} />
          </div>
          <button className="btn btn-primary" onClick={run} disabled={running} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {running ? '⏳ Generiere...' : '🤖 Reviews generieren'}
          </button>
        </div>
      </div>

      {/* Auto-Review manuell auslösen */}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
        <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'Syne', textTransform: 'uppercase', marginBottom: 8 }}>
          Auto-Review — jetzt ausführen
        </div>
        <p style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 10 }}>
          Generiert Reviews für alle Accounts mit aktivem Auto-Review (≥3 Tage alte Bestellungen). Unklare Produkttitel werden ASIN-weit einmal geprüft und bei Bedarf später erneut kontrolliert.
        </p>
        <button className="btn btn-primary" onClick={runAuto} disabled={runningAuto} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--accent2)', border: 'none' }}>
          {runningAuto ? '⏳ Läuft...' : '▶ Auto-Review jetzt ausführen'}
        </button>
      </div>

      {result && (
        <div style={{ background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.2)', borderRadius: 8, padding: '12px 16px', fontSize: 12, marginTop: 14 }}>
          <div style={{ fontFamily: 'Syne', fontWeight: 700, marginBottom: 6, color: 'var(--accent2)' }}>✅ Fertig</div>
          <div style={{ color: 'var(--text2)' }}>
            {result.generated} generiert · {result.waiting_product_info || 0} warten auf Produktinfos · {result.needs_info} brauchen Infos · {result.errors} Fehler · {result.processed || result.skipped || 0} verarbeitet/übersprungen
          </div>
        </div>
      )}
    </>
  );
}


// ── Main Settings Component ─────────────────────────────────────────────────
export default function Settings() {
  const [form, setForm] = useState({
    bot_token:'', telegram_chat_id:'', notification_delay:0,
    anthropic_api_key:'', openai_api_key:'', preferred_ai:'anthropic',
    default_commission_text:25, default_commission_bild:30, default_commission_video:35,
    default_tax_ref:true, default_status:'Bestellt', default_platform:'WeChat',
  });
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [toast, setToast] = useState(null);
  const [group, setGroup] = useState(null);
  const [groupChatId, setGroupChatId] = useState('');
  const [groupName, setGroupName] = useState('');
  const [groupSaving, setGroupSaving] = useState(false);

  // Personas State
  const [personas, setPersonas] = useState({});
const [savingAccount, setSavingAccount] = useState(null);
const [accounts, setAccounts] = useState([]);
const [aiConfig, setAiConfig] = useState(null);
const [stripeConfig, setStripeConfig] = useState(null);
const [stripeForm, setStripeForm] = useState({ api_key:'', webhook_secret:'' });
const [stripeSaving, setStripeSaving] = useState(false);
const [stripeTesting, setStripeTesting] = useState(false);
const [stripeDisconnecting, setStripeDisconnecting] = useState(false);

  useEffect(() => {
    Promise.all([
      API.get('/settings'),
      API.get('/reviews/personas'),
      API.get('/accounts'),
      API.get('/reviews/ai-config').catch(() => ({ data: null })),
      API.get('/settings/stripe').catch(() => ({ data: { stripe: null } }))
    ]).then(([settingsRes, personasRes, accountsRes, aiConfigRes, stripeRes]) => {
      const s = settingsRes.data.settings;
      setIsAdmin(!!s.is_admin);
      setGroup(settingsRes.data.group || null);
      if (settingsRes.data.group) setGroupChatId(settingsRes.data.group.group_chat_id || '');
      setForm({
        bot_token: s.bot_token||'', telegram_chat_id: s.telegram_chat_id||'',
        notification_delay: s.notification_delay||0,
        anthropic_api_key: s.anthropic_api_key||'', openai_api_key: s.openai_api_key||'',
        preferred_ai: s.preferred_ai||'anthropic',
        default_commission_text: s.default_commission_text??25,
        default_commission_bild: s.default_commission_bild??30,
        default_commission_video: s.default_commission_video??35,
        default_tax_ref: s.default_tax_ref??true,
        default_status: s.default_status||'Bestellt',
        default_platform: s.default_platform||'WeChat',
        
      });

      const pMap = {};
      for (const p of (personasRes.data.personas || [])) pMap[p.account_name] = p;
      setPersonas(pMap);
      setAccounts(accountsRes.data.accounts || []);
      setAiConfig(aiConfigRes.data || null);
      setStripeConfig(stripeRes.data?.stripe || null);
      setLoading(false);
    });
  }, []);

  const showToast = (msg, type='success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const save = async () => {
    setSaving(true);
    try {
      await API.patch('/settings', form);
      showToast('Einstellungen gespeichert!');
    } catch { showToast('Fehler beim Speichern.', 'error'); }
    finally { setSaving(false); }
  };

  const testTelegram = async () => {
    setTesting(true);
    try {
      await API.post('/settings/test-telegram');
      showToast('✅ Testnachricht gesendet!');
    } catch (err) {
      showToast(err.response?.data?.error||'Fehler beim Senden.', 'error');
    } finally { setTesting(false); }
  };

  const saveStripe = async () => {
    setStripeSaving(true);
    try {
      const res = await API.put('/settings/stripe', {
        api_key: stripeForm.api_key || null,
        webhook_secret: stripeForm.webhook_secret || null,
        enabled: true
      });
      setStripeConfig(res.data.stripe || null);
      setStripeForm({ api_key:'', webhook_secret:'' });
      showToast('✅ Stripe-Integration gespeichert.');
    } catch (err) {
      showToast(err.response?.data?.message || 'Stripe-Integration konnte nicht gespeichert werden.', 'error');
    } finally {
      setStripeSaving(false);
    }
  };

  const testStripe = async () => {
    setStripeTesting(true);
    try {
      const res = await API.post('/settings/stripe/test');
      setStripeConfig(res.data.stripe || stripeConfig);
      showToast('✅ Stripe-Verbindung erfolgreich getestet.');
    } catch (err) {
      showToast(err.response?.data?.message || 'Stripe-Verbindungstest fehlgeschlagen.', 'error');
    } finally {
      setStripeTesting(false);
    }
  };

  const disconnectStripe = async () => {
    if (!window.confirm('Stripe-Verbindung wirklich trennen? Bereits importierte Zahlungsblöcke bleiben erhalten.')) return;
    setStripeDisconnecting(true);
    try {
      await API.delete('/settings/stripe');
      const res = await API.get('/settings/stripe');
      setStripeConfig(res.data.stripe || null);
      setStripeForm({ api_key:'', webhook_secret:'' });
      showToast('Stripe-Verbindung getrennt.');
    } catch (err) {
      showToast(err.response?.data?.message || 'Stripe-Verbindung konnte nicht getrennt werden.', 'error');
    } finally {
      setStripeDisconnecting(false);
    }
  };

  const copyStripeWebhook = async () => {
    const url = stripeConfig?.webhook_url || '';
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      showToast('Webhook-URL kopiert.');
    } catch {
      showToast('Webhook-URL konnte nicht kopiert werden.', 'error');
    }
  };

  const savePersona = async (accountName, p) => {
    setSavingAccount(accountName);
    try {
      await API.put(`/reviews/personas/${encodeURIComponent(accountName)}`, p);
      setPersonas(prev => ({ ...prev, [accountName]: { ...p, account_name: accountName } }));
    } catch { showToast('Fehler beim Speichern der Persona.', 'error'); }
    finally { setSavingAccount(null); }
  };


  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));
  const setVal = (key) => (val) => setForm(f => ({ ...f, [key]: val }));

  if (loading) return (
    <div className="page" style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh' }}>
      <p style={{ color:'var(--text3)' }}>Laden...</p>
    </div>
  );

  return (
    <div className="page" style={{ maxWidth:760 }}>
      {/* Toast */}
      {toast && (
        <div style={{ position:'fixed', top:20, right:20, zIndex:1000, background: toast.type==='error' ? 'var(--danger)':'var(--accent2)', color: toast.type==='error' ? 'white':'#000', padding:'12px 20px', borderRadius:10, display:'flex', alignItems:'center', gap:8, fontFamily:'Syne', fontWeight:600, fontSize:13, boxShadow:'0 4px 20px rgba(0,0,0,0.3)' }}>
          {toast.type==='error' ? <AlertCircle size={16}/> : <CheckCircle size={16}/>}
          {toast.msg}
        </div>
      )}

      <div className="page-header">
        <div>
          <h1 className="page-title">Einstellungen</h1>
          <p style={{ color:'var(--text2)', marginTop:4, fontSize:13 }}>Bot, API Keys, Stripe, Personas und Benachrichtigungen konfigurieren</p>
        </div>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          <Save size={14}/>{saving ? 'Speichern...':'Speichern'}
        </button>
      </div>

      {/* ─── STANDARDWERTE ─────────────────────────────────────────────────── */}
      <Section title="Standardwerte" icon={Sliders}>
        <p style={{ color:'var(--text3)', fontSize:12, marginBottom:20 }}>
          Diese Werte werden beim Anlegen neuer Bestellungen automatisch vorausgewählt.
        </p>
        <Field label="Commission pro Review-Typ (€)">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
            {[{ key:'default_commission_text', label:'📝 Text' },{ key:'default_commission_bild', label:'🖼️ Bild' },{ key:'default_commission_video', label:'🎬 Video' }].map(({ key, label }) => (
              <div key={key}>
                <label style={{ display:'block', fontSize:11, color:'var(--text3)', marginBottom:4 }}>{label}</label>
                <input type="number" value={form[key]} onChange={e => setForm(f=>({...f,[key]:parseInt(e.target.value)||0}))} min="0" step="1" style={{ textAlign:'center' }}/>
              </div>
            ))}
          </div>
        </Field>
        <Field label="Standard Provision" hint="Wird als Vorauswahl bei neuen Bestellungen gesetzt">
          <PillGroup options={[true,false]} value={form.default_tax_ref} onChange={setVal('default_tax_ref')} renderLabel={v=>v?'Ja':'Nein'}/>
        </Field>
        <Field label="Privat-Schwellenwert (€)" hint="Bestellungen über diesem Betrag werden automatisch als 'Privat' markiert">
          <input type="number" value={form.private_threshold??40} onChange={e=>setForm(f=>({...f,private_threshold:parseInt(e.target.value)||0}))} min="0" step="1" style={{ maxWidth:120 }}/>
        </Field>
        <Field label="Standard Status">
          <PillGroup options={STATUS_OPTIONS} value={form.default_status} onChange={setVal('default_status')}/>
        </Field>
        <Field label="Standard Messenger">
          <PillGroup options={PLATFORM_OPTIONS} value={form.default_platform} onChange={setVal('default_platform')} renderLabel={p=>`${MESSENGER_EMOJIS[p]} ${p}`}/>
        </Field>
      </Section>

      {/* Accounts und Import-Adressen werden ausschließlich zentral im Admin-Panel verwaltet. */}
      {/* ─── PERSONAS ──────────────────────────────────────────────────────────── */}
<Section title="Personas & Auto-Review" icon={Users}>
  <p style={{ color:'var(--text3)', fontSize:12, marginBottom:16 }}>
    Definiere für jeden deiner Accounts eine Persona. Accounts und Import-Adressen werden zentral vom Admin verwaltet.
  </p>
  {accounts.map(acc => (
    <PersonaCard
      key={acc.id}
      accountName={acc.account_name}
      displayName={acc.display_name}
      persona={personas[acc.account_name]}
      onSave={savePersona}
      savingAccount={savingAccount}
    />
  ))}
  {accounts.length === 0 && (
    <div style={{ color:'var(--text3)', fontSize:13, padding:20, textAlign:'center' }}>
      Noch keine Accounts vorhanden. Bitte den Admin bitten, einen Account anzulegen.
    </div>
  )}
</Section>

      {/* ─── KI API KEYS ───────────────────────────────────────────────────── */}
      <Section title="KI API Keys" icon={Key}>
        <div style={{ background:'rgba(124,106,247,0.08)', border:'1px solid rgba(124,106,247,0.2)', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:12, color:'var(--text2)' }}>
          ℹ️ Eigene API-Keys sind optional. Ohne eigenen Key nutzt die Review-Engine serverseitig den zentralen Review-Key; Personas und Reviews bleiben trotzdem strikt deinem Benutzer zugeordnet.
        </div>
        {aiConfig?.automaticRouting && (
          <div style={{ background:'rgba(74,222,128,0.07)', border:'1px solid rgba(74,222,128,0.22)', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:12, color:'var(--text2)', lineHeight:1.6 }}>
            <div style={{ color:'var(--accent2)', fontFamily:'Syne', fontWeight:700, marginBottom:4 }}>✓ Automatische Modellwahl aktiv</div>
            <div>Produktprüfung: <code>{aiConfig.openai?.product}</code></div>
            <div>Review: <code>{aiConfig.openai?.review}</code></div>
            <div>Fallback bei schwierigen/zu ähnlichen Ergebnissen: <code>{aiConfig.openai?.fallback}</code></div>
            <div style={{ color:'var(--text3)', marginTop:4 }}>Unklare ASINs werden zentral einmal geprüft und bei Bedarf nach {aiConfig.productRetryDays || 7} Tagen erneut kontrolliert.</div>
          </div>
        )}
        <Field label="Bevorzugte KI">
          <select value={form.preferred_ai} onChange={set('preferred_ai')} style={{ maxWidth:200 }}>
            <option value="anthropic">Claude (Anthropic)</option>
            <option value="openai">ChatGPT (OpenAI)</option>
          </select>
        </Field>
        <Field label="Anthropic API Key (Claude)" hint="Von console.anthropic.com — Format: sk-ant-api03-...">
          <SecretInput value={form.anthropic_api_key} onChange={set('anthropic_api_key')} placeholder="sk-ant-api03-..."/>
        </Field>
        <Field label="OpenAI API Key (ChatGPT)" hint="Von platform.openai.com — Format: sk-proj-...">
          <SecretInput value={form.openai_api_key} onChange={set('openai_api_key')} placeholder="sk-proj-..."/>
        </Field>
      </Section>

      {/* ─── KI BATCH GENERIEREN ───────────────────────────────────────────── */}
<Section title="KI Reviews generieren" icon={Bot}>
  <p style={{ color:'var(--text3)', fontSize:12, marginBottom:16 }}>
    Generiere KI-Reviews für alle Bestellungen ab einem bestimmten Datum. Nur Bestellungen ohne vorhandenen Review werden verarbeitet.
  </p>
  <BatchGenerateSection showToast={showToast} />
</Section>

      {/* ─── STRIPE ───────────────────────────────────────────────────────── */}
      <Section title="Stripe Integration" icon={CreditCard}>
        <div style={{ background:'rgba(124,106,247,0.08)', border:'1px solid rgba(124,106,247,0.2)', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:12, color:'var(--text2)', lineHeight:1.6 }}>
          <div style={{ fontFamily:'Syne', fontWeight:700, color: stripeConfig?.configured ? 'var(--accent2)' : 'var(--text2)', marginBottom:4 }}>
            {stripeConfig?.configured ? '● Stripe verbunden' : '○ Stripe nicht verbunden'}
            {stripeConfig?.legacy_env ? ' · Legacy ENV' : ''}
          </div>
          <div>Jeder Benutzer verbindet sein eigenes Stripe-Konto. API- und Webhook-Secrets werden serverseitig verschlüsselt gespeichert und niemals wieder vollständig angezeigt.</div>
        </div>

        {stripeConfig?.api_key_hint && (
          <div style={{ background:'var(--bg3)', borderRadius:8, padding:12, marginBottom:12, fontSize:12, color:'var(--text2)' }}>
            <div><b>API-Key:</b> <code>{stripeConfig.api_key_hint}</code></div>
            {stripeConfig.stripe_account_id && <div style={{ marginTop:4 }}><b>Stripe Account:</b> <code>{stripeConfig.stripe_account_id}</code></div>}
            <div style={{ marginTop:4 }}>
              <b>Webhook:</b>{' '}
              {stripeConfig.webhook_secret_configured
                ? <span style={{ color:'var(--accent2)' }}>✓ Signing Secret gespeichert</span>
                : <span style={{ color:'var(--warning, #f59e0b)' }}>noch nicht konfiguriert</span>}
            </div>
            {stripeConfig.last_tested_at && (
              <div style={{ marginTop:4, color:'var(--text3)' }}>Letzter Verbindungstest: {new Date(stripeConfig.last_tested_at).toLocaleString('de-DE')}</div>
            )}
            {stripeConfig.last_error && (
              <div style={{ marginTop:6, color:'var(--danger)' }}>Letzter Fehler: {stripeConfig.last_error}</div>
            )}
          </div>
        )}

        <Field
          label={stripeConfig?.configured ? 'Stripe API Key ändern' : 'Stripe API Key'}
          hint="Empfohlen: Restricted Key. Benötigte Leserechte: Payment Intents, Charges, Balance Transactions und Checkout Sessions."
        >
          <SecretInput
            value={stripeForm.api_key}
            onChange={e => setStripeForm(f => ({ ...f, api_key:e.target.value }))}
            placeholder={stripeConfig?.api_key_hint || 'rk_live_... oder sk_live_...'}
          />
        </Field>

        {stripeConfig?.webhook_url && (
          <Field label="Webhook URL" hint="Diese URL im Stripe Dashboard als Webhook-Endpunkt anlegen. Ereignisse: checkout.session.completed, payment_intent.succeeded, charge.succeeded.">
            <div style={{ display:'flex', gap:8 }}>
              <input value={stripeConfig.webhook_url} readOnly style={{ flex:1 }} />
              <button className="btn btn-ghost" type="button" onClick={copyStripeWebhook} title="Webhook-URL kopieren">
                <Copy size={14}/> Kopieren
              </button>
            </div>
          </Field>
        )}

        {!stripeConfig?.legacy_env && (
          <Field
            label={stripeConfig?.webhook_secret_configured ? 'Webhook Signing Secret ändern' : 'Webhook Signing Secret'}
            hint="Nach dem Anlegen des Webhooks zeigt Stripe ein Signing Secret im Format whsec_...."
          >
            <SecretInput
              value={stripeForm.webhook_secret}
              onChange={e => setStripeForm(f => ({ ...f, webhook_secret:e.target.value }))}
              placeholder={stripeConfig?.webhook_secret_hint || 'whsec_...'}
            />
          </Field>
        )}

        {stripeConfig?.legacy_env && (
          <div style={{ background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.25)', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:12, color:'var(--text2)', lineHeight:1.6 }}>
            Dein bestehendes Darek-Stripe läuft weiter über die Server-ENV und den bisherigen Webhook. Du musst es für diesen Patch nicht neu einrichten. Wenn du später auf die Benutzerintegration wechseln möchtest, trägst du hier einen neuen API-Key ein.
          </div>
        )}

        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          <button
            className="btn btn-primary"
            type="button"
            onClick={saveStripe}
            disabled={stripeSaving || (!stripeForm.api_key && !stripeConfig?.configured) || (stripeConfig?.legacy_env && !stripeForm.api_key)}
          >
            <Save size={14}/>{stripeSaving ? 'Speichere...' : stripeConfig?.configured && !stripeConfig?.legacy_env ? 'Stripe speichern' : 'API verbinden'}
          </button>

          {stripeConfig?.configured && (
            <button className="btn btn-ghost" type="button" onClick={testStripe} disabled={stripeTesting}>
              <RefreshCw size={14}/>{stripeTesting ? 'Teste...' : 'Verbindung testen'}
            </button>
          )}

          {stripeConfig?.configured && !stripeConfig?.legacy_env && (
            <button className="btn btn-ghost" type="button" onClick={disconnectStripe} disabled={stripeDisconnecting} style={{ color:'var(--danger)' }}>
              <Trash2 size={14}/>{stripeDisconnecting ? 'Trenne...' : 'Verbindung trennen'}
            </button>
          )}
        </div>

        {!stripeConfig?.configured && (
          <div style={{ marginTop:14, color:'var(--text3)', fontSize:11, lineHeight:1.6 }}>
            Ablauf: 1) API-Key speichern → 2) angezeigte Webhook-URL in Stripe anlegen → 3) <code>whsec_...</code> hier eintragen → 4) Verbindung testen.
          </div>
        )}
      </Section>

      {/* ─── TELEGRAM BOT ──────────────────────────────────────────────────── */}
      <Section title="Telegram Bot" icon={Bot}>
        <Field label="Bot Token" hint="Von @BotFather erhalten. Format: 123456:ABC-DEF...">
          <SecretInput value={form.bot_token} onChange={set('bot_token')} placeholder="123456789:AAHRtTP6tco7iNUA6vP0qHK8uvgxwAEPV0w"/>
        </Field>
        <Field label="Telegram Chat ID" hint="Deine persönliche Chat ID. Bekommst du über @userinfobot">
          <input value={form.telegram_chat_id} onChange={set('telegram_chat_id')} placeholder="1041057563"/>
        </Field>
        {isAdmin && (
          <Field label="⚙️ Benachrichtigungs-Delay (Sekunden) — Admin" hint="Verzögerung für andere User bei Wishlist Matches.">
            <input type="number" value={form.notification_delay} onChange={set('notification_delay')} min="0" max="300" style={{ maxWidth:120 }}/>
          </Field>
        )}
        <button className="btn btn-ghost" onClick={testTelegram} disabled={testing||!form.bot_token||!form.telegram_chat_id}>
          <Send size={14}/>{testing ? 'Sende...':'Testnachricht senden'}
        </button>
      </Section>

      {/* ─── TELEGRAM GRUPPE ───────────────────────────────────────────────── */}
      <Section title="Telegram Gruppe" icon={Bot}>
        {group ? (
          <div>
            <div style={{ background:'var(--bg3)', borderRadius:8, padding:12, marginBottom:12 }}>
              <div style={{ fontSize:12, color:'var(--accent2)', fontWeight:700, marginBottom:4 }}>✅ Gruppe verbunden</div>
              <div style={{ fontSize:12, color:'var(--text2)' }}>Chat ID: <code>{group.group_chat_id}</code></div>
              {group.name && <div style={{ fontSize:12, color:'var(--text2)' }}>Name: {group.name}</div>}
              {group.member_emails?.filter(Boolean).length > 0 && (
                <div style={{ fontSize:12, color:'var(--text3)', marginTop:4 }}>
                  Mitglieder: {group.member_emails.filter(Boolean).join(', ')}
                </div>
              )}
            </div>
            <button className="btn btn-ghost" style={{ color:'var(--accent3)' }} onClick={async () => {
              if (!window.confirm('Gruppe verlassen?')) return;
              await API.delete('/settings/leave-group');
              setGroup(null); setGroupChatId('');
              showToast('Gruppe verlassen.');
            }}>Gruppe verlassen</button>
          </div>
        ) : (
          <div>
            <p style={{ color:'var(--text3)', fontSize:12, marginBottom:12 }}>
              Verbinde eine Telegram-Gruppe um Wishlist & Agenten mit anderen Usern zu teilen.
              Die Chat ID der Gruppe bekommst du über @userinfobot wenn du ihn in die Gruppe einlädst.
            </p>
            <Field label="Gruppen Chat ID">
              <input value={groupChatId} onChange={e => setGroupChatId(e.target.value)} placeholder="-1001234567890"/>
            </Field>
            <Field label="Gruppen Name (optional)">
              <input value={groupName} onChange={e => setGroupName(e.target.value)} placeholder="Vine Team"/>
            </Field>
            <button className="btn btn-primary" disabled={!groupChatId || groupSaving} onClick={async () => {
              setGroupSaving(true);
              try {
                const res = await API.post('/settings/join-group', { group_chat_id: groupChatId, group_name: groupName || null });
                showToast(`Gruppe verbunden! ${res.data.members.length} Mitglied(er).`);
                const settingsRes = await API.get('/settings');
                setGroup(settingsRes.data.group || null);
              } catch(e) {
                showToast('Fehler beim Verbinden.', 'error');
              } finally { setGroupSaving(false); }
            }}>
              {groupSaving ? 'Verbinde...' : 'Gruppe verbinden'}
            </button>
          </div>
        )}
      </Section>

      {/* ─── WEBHOOK ───────────────────────────────────────────────────────── */}
      <Section title="Webhook Info" icon={Bot}>
        <div style={{ background:'var(--bg3)', borderRadius:8, padding:16 }}>
          <p style={{ color:'var(--text3)', fontSize:12, marginBottom:8 }}>Webhook URL für deinen Telegram Bot:</p>
          <code style={{ color:'var(--accent)', fontSize:12, background:'rgba(124,106,247,0.1)', padding:'4px 8px', borderRadius:4 }}>
            https://DEINE-DOMAIN.de/webhook/telegram/1
          </code>
          <p style={{ color:'var(--text3)', fontSize:11, marginTop:8 }}>Ersetze DEINE-DOMAIN mit deiner Ionos Domain sobald deployed.</p>
        </div>
      </Section>

      <div style={{ display:'flex', justifyContent:'flex-end', marginTop:8 }}>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          <Save size={14}/>{saving ? 'Speichern...':'Alle Einstellungen speichern'}
        </button>
      </div>
    </div>
  );
}